import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { assert } from '../../errors';
import {
    ExternalFinancialEntity,
    getFEBaseConfig,
    isDepositoryAccount,
} from '../../financialEntities';
import { ConfirmAddedFinancialEntityDocument } from '../../graphql/generated';
import { sortBy } from '../../util/arrays';
import { isNullish } from '../../util/types';
import {
    assetsFromFEs,
    configByFECombinedType,
    debtsFromFEs,
    getFECombinedType,
    getFECombinedTypeOptions,
    Institution,
    useFinancialEntitiesContext,
} from '../financialEntities';

const DEFAULT_IS_PRIVATE = false;
const DEFAULT_IS_PARTNER_JOINT_OWNER = null;

export const jointOwnerYesOptionLabel =
    'my partner is the other legal owner of this joint account';
export const jointOwnerYesOptionDescription =
    'This authorizes your partner to deposit money from this account into Plenty, and withdraw money from Plenty into this account';
export const jointOwnerNoOptionLabel =
    'someone else is the other legal owner of this joint account';

const consentContents = `Is your partner a legal account owner of this joint account?
- [x] Yes, ${jointOwnerYesOptionLabel}
${jointOwnerYesOptionDescription}
- [ ] No, ${jointOwnerNoOptionLabel}`;

export type AccountLinkingConfirmationSubstep =
    | 'INITIAL'
    | 'JOINT_OWNER'
    | 'READ_MORE';

export const useAccountLinkingConfirmationFlow = ({
    accounts,
    privateFinancialEntityIds,
    onComplete,
    nextButtonText,
    startWithJointOwnerSubstep,
}: {
    accounts: ExternalFinancialEntity[];
    privateFinancialEntityIds?: string[];
    onComplete: VoidFunction;
    nextButtonText?: string;
    startWithJointOwnerSubstep?: boolean;
}) => {
    assert(accounts.length > 0); // Subsequent logic requires 1+ accounts
    const [privateAccountsCount, setPrivateAccountsCount] = useState(0);
    const [jointAccountsCount, setJointAccountsCount] = useState(0);
    const [confirmAddedFinancialEntity] = useMutation(
        ConfirmAddedFinancialEntityDocument
    );
    const [substep, setSubstep] = useState<AccountLinkingConfirmationSubstep>(
        startWithJointOwnerSubstep ? 'JOINT_OWNER' : 'INITIAL'
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentAccountIndex, setCurrentAccountIndex] = useState(0);
    const currentAccount = accounts[currentAccountIndex];
    const isLastAccount = currentAccountIndex === accounts.length - 1;
    const [ownershipType, setOwnershipType] = useState(
        startWithJointOwnerSubstep ? 'JOINT' : currentAccount.ownershipType
    );
    const [feCombinedType, setFECombinedType] = useState(
        getFECombinedType(currentAccount.info)
    );
    const [isPrivate, setIsPrivate] = useState(
        privateFinancialEntityIds?.includes(currentAccount.id)
            ? true
            : DEFAULT_IS_PRIVATE
    );
    const [isPartnerJointOwner, setIsPartnerJointOwner] = useState<
        boolean | null
    >(DEFAULT_IS_PARTNER_JOINT_OWNER);

    const feCombinedTypeOptions = getFECombinedTypeOptions(
        currentAccount.type,
        ownershipType
    );

    const eyebrowHeaderStr =
        accounts.length > 1
            ? `Account ${currentAccountIndex + 1} of ${accounts.length}`
            : undefined;

    const headerStr =
        substep === 'INITIAL'
            ? `Confirm the information is correct for ${accounts.length > 1 ? 'each' : 'this'} account`
            : 'Is your partner a legal account owner of this joint account?';

    const primaryButtonText = isLastAccount
        ? nextButtonText ?? 'Looks good'
        : 'Next';

    const isPrimaryButtonVisible =
        substep === 'INITIAL' || substep === 'JOINT_OWNER';

    const isPrimaryButtonDisabled =
        isSubmitting ||
        (substep === 'JOINT_OWNER' && isNullish(isPartnerJointOwner));

    const onNext = () => {
        if (
            substep === 'INITIAL' &&
            ownershipType === 'JOINT' &&
            !isPrivate &&
            isDepositoryAccount(currentAccount)
        ) {
            // Go to the JOINT_OWNER substep for this account
            setSubstep('JOINT_OWNER');
        } else {
            // Finished this account, so run mutation and go to the next account
            const feCombinedTypeConfig = configByFECombinedType[feCombinedType];
            setIsSubmitting(true);

            // If partner is joint owner, account must be SHARED
            const finalIsPrivate = isPartnerJointOwner ? false : isPrivate;

            if (finalIsPrivate) {
                setPrivateAccountsCount((prev) => prev + 1);
            }

            if (ownershipType === 'JOINT') {
                setJointAccountsCount((prev) => prev + 1);
            }

            confirmAddedFinancialEntity({
                variables: {
                    financialEntityId: currentAccount.id,
                    ...(feCombinedTypeConfig &&
                    feCombinedType !== getFECombinedType(currentAccount.info)
                        ? {
                              financialEntityType: feCombinedTypeConfig.type,
                              financialEntitySubtype:
                                  feCombinedTypeConfig.subtype,
                          }
                        : undefined),
                    ownershipType,
                    isPrivate: finalIsPrivate,
                    isPartnerJointOwner,
                    consent: isPartnerJointOwner
                        ? {
                              title: '20240130-partner-legal-owner-of-external-joint-account',
                              method: 'BUTTON',
                              contents: consentContents,
                              buttonText: primaryButtonText,
                          }
                        : undefined,
                },
            })
                .then(() => {
                    if (isLastAccount) {
                        // Finished the last account
                        onComplete();
                    } else {
                        // Reset state for the next account
                        const nextAccountIndex = currentAccountIndex + 1;
                        const nextAccount = accounts[nextAccountIndex];
                        setCurrentAccountIndex(nextAccountIndex);
                        setSubstep('INITIAL');
                        setOwnershipType(nextAccount.ownershipType);
                        setFECombinedType(getFECombinedType(nextAccount.info));
                        setIsPrivate(
                            privateFinancialEntityIds?.includes(nextAccount.id)
                                ? true
                                : DEFAULT_IS_PRIVATE
                        );
                        setIsPartnerJointOwner(DEFAULT_IS_PARTNER_JOINT_OWNER);
                    }
                })
                .finally(() => setIsSubmitting(false));
        }
    };

    const onBack =
        substep === 'JOINT_OWNER' && !startWithJointOwnerSubstep
            ? () => setSubstep('INITIAL')
            : substep === 'READ_MORE'
              ? () => setSubstep('JOINT_OWNER')
              : undefined;

    const onClickReadMore = () => {
        setSubstep('READ_MORE');
    };

    return {
        currentAccount,
        substep,
        ownershipType,
        setOwnershipType,
        feCombinedTypeOptions,
        feCombinedType,
        setFECombinedType,
        isPrivate,
        setIsPrivate,
        isPartnerJointOwner,
        setIsPartnerJointOwner,
        isSubmitting,
        eyebrowHeaderStr,
        headerStr,
        primaryButtonText,
        isPrimaryButtonVisible,
        isPrimaryButtonDisabled,
        onNext,
        onBack,
        onClickReadMore,
        privateAccountsCount,
        jointAccountsCount,
    };
};

export const useAccountsForLinkingConfirmation = ({
    financialEntityIds,
}: {
    financialEntityIds: string[];
}) => {
    const { fes } = useFinancialEntitiesContext();

    // Use unfiltered data in this specific context to allow member
    // the opportunity to decide sharing permissions for *all* added FEs.
    if (!fes.unfilteredData) return null;
    const assets = assetsFromFEs(fes.unfilteredData.financialEntities);
    const debts = debtsFromFEs(fes.unfilteredData.financialEntities);
    const allFEs = [...assets, ...debts];
    const filteredFEs = allFEs.filter(({ id }) =>
        financialEntityIds.includes(id)
    );
    return sortBy(filteredFEs, (fe) => getAccountSortKey(fe));
};

/**
 * Returns a string key to be used for sorting an array of FEs, e.g.
 *
 * Regions Bank|asset|CashAccount|CheckingAccount|Plaid 0% Interest Checking
 * Regions Bank|asset|CashAccount|SavingsAccount|Plaid 0.1% Interest Saving
 * Regions Bank|asset|Retirement|RetirementAccount|Plaid 401k
 * Regions Bank|asset|Retirement|RetirementAccount|Plaid IRA
 * Regions Bank|debt|CreditCard|CreditCard|Plaid 12.5% APR Interest Credit Card
 * Regions Bank|debt|Debt|HomeMortgage|Plaid Mortgage
 */
const getAccountSortKey = (fe: ExternalFinancialEntity) =>
    `${fe.institution?.name}|${getFEBaseConfig(fe.type).financialType}|${getFEBaseConfig(fe.type).category}|${fe.type}|${fe.name}`;

export const getConnectedInstitutionText = (
    institution: Institution,
    platform: 'MOBILE' | 'WEB'
) => {
    if (institution.sharedCount > 0 && institution.privateCount > 0) {
        return platform === 'WEB'
            ? 'Some accounts are marked as “shared” and some are “private”. Private accounts are only visible in the “What’s Mine” view. Switch over in the toggle on the left sidebar.'
            : 'Some accounts are marked as “shared” and some are “private”. Private accounts are only visible in the “My money” view. Switch over in the toggle after tapping on your avatar.';
    } else if (institution.sharedCount > 0 && institution.privateCount === 0) {
        return platform === 'WEB'
            ? 'Your accounts are marked as “shared” and will appear in the “What’s Ours” and “What’s Mine” view. Switch over in the toggle on the left sidebar.'
            : 'Your accounts are marked as “shared” and will appear in the “Our money” and “My money” view. Switch over in the toggle after tapping on your avatar.';
    } else if (institution.privateCount > 0 && institution.sharedCount === 0) {
        return platform === 'WEB'
            ? 'Your accounts are marked as “private” and will only appear in the “What’s Mine” view. Switch over in the toggle on the left sidebar.'
            : 'Your accounts are marked as “private” and will only appear in the “My money” view. Switch over in the toggle after tapping on your avatar.';
    }
};

export * from './getLinkedInstitutionRowSubtitle';
